/**
 * Export セクション: 書き出し尺コントロール + モード dropdown + PNG / WebM
 * ダウンロードボタン + 進捗表示。
 *
 * 設計書 FR-18 / FR-19 / FR-20 / 10.2。Phase D で `exportMode` dropdown、
 * Phase I 再設計で WebM ボタンと進捗行、Phase J で Pre-Pulse /
 * Detonate Speed が追加。**Phase K2 Step 2 で GIF 書き出しは完全撤去**
 * （gif.ts / gif.js 依存も削除済み）、書き出しは PNG + WebM の 2 系統に整理。
 *
 * 理由: UI 層は `render` / `export` モジュールに import 依存を持たない。
 * main.ts が wire 時に `onExportPng` / `onExportWebM` に実際の書き出し関数を
 * 差し込む。
 *
 * セクションの縦配置:
 * ```
 *   Pre-Pulse        [0s] [1s] [2s] [3s]    ← segmented（4 段階）
 *   Detonate         [2.0s ▼]               ← dropdown（5/4/3/2/1/0.5s）
 *   Mode             [Detonate ▼]           ← dropdown
 *   [PNG] [WebM]                             ← 2 列ボタン（K2 で 3→2 列）
 *   (progress line)                          ← hidden by default
 * ```
 */

import type { AppState, Store } from '../../state';

/**
 * `exportMode` dropdown の選択肢。値は `AppState.exportMode` の union と 1:1。
 * ラベルは設計書 Phase D 指示（英日併記）に従う。
 */
const EXPORT_MODE_OPTIONS: ReadonlyArray<{ value: AppState['exportMode']; label: string }> = [
  { value: 'detonate', label: 'Detonate' },
  { value: 'idle-loop', label: 'Idle Loop' },
  { value: 'full-story', label: 'Full Story' },
];

/**
 * Pre-Pulse の許容秒数（Phase J）。
 * 0 = 前置きなし（Phase I 以前の挙動）、1/2/3 = 呼吸を見せる秒数。
 * 書き出しのみに効き、DETONATE ボタンプレビューには影響しない。
 */
const PRE_PULSE_OPTIONS: ReadonlyArray<number> = [0, 1, 2, 3];

/**
 * Detonate Speed の許容秒数（Phase J）。
 * 秒数の降順で並べているのは「遅い → 速い」の感覚順（左端ほど緩やか）。
 * DETONATE プレビュー + 書き出しの両方に効く。
 */
const DETONATE_DURATION_OPTIONS: ReadonlyArray<number> = [5, 4, 3, 2, 1, 0.5];

/** `mountExportButtons` の戻り値。 */
export interface ExportButtonsHandle {
  /** teardown: DOM + listener 解除。 */
  destroy: () => void;
  /** PNG ボタン click 時に呼ぶ callback を登録する。上書き式。 */
  onExportPng: (cb: () => void) => void;
  /** WebM ボタン click 時に呼ぶ callback を登録する。上書き式（Phase I 再設計）。 */
  onExportWebM: (cb: () => void) => void;
  /**
   * 進捗表示を更新する（Phase I 再設計）。
   *
   * `label` が空文字列 or null のときは進捗行を非表示にする（idle 状態）。
   * WebM の書き出しは数秒かかるので、ユーザが「動いているか」を視認できる
   * ようボタン下に控えめに表示する。
   */
  setProgress: (label: string | null) => void;
  /**
   * ボタン + コントロールの disabled 状態を一括切り替え（Phase I 再設計 /
   * Phase J で尺コントロールも対象に追加）。書き出し処理中に他モードを
   * 起動されるのを防ぐ + 尺を途中で変えられるのを防ぐ。
   */
  setBusy: (busy: boolean) => void;
}

/**
 * Export セクションをマウントする。
 *
 * Phase J で Pre-Pulse segmented button と Detonate Speed dropdown が加わり、
 * セクションは上から「尺コントロール（2 行）→ Mode dropdown → 書き出しボタン
 * （PNG/WebM）→ 進捗行」の 5 ブロック構成。順序は
 * 「時間パラメータ → モード選択 → 実行」の意味順。
 * Phase K2 Step 2 で GIF ボタンは撤去済み。
 *
 * @param root - Controls 内の新規 `<div>` を想定
 * @param store - グローバルストア
 * @returns `ExportButtonsHandle`
 */
export function mountExportButtons(root: HTMLElement, store: Store<AppState>): ExportButtonsHandle {
  const section = document.createElement('div');

  const label = document.createElement('div');
  label.className = 'section-label';
  label.textContent = 'Export';
  section.appendChild(label);

  // ── Pre-Pulse segmented (Phase J) ───────────────────────────
  // 0/1/2/3 の 4 段階。`.segmented` のバリエーションとして `segmented-4`
  // を当てて 4 等分 grid にする（.segmented は既定 5 等分）。
  const prePulseRow = document.createElement('div');
  prePulseRow.className = 'font-picker-row';
  const prePulseLabel = document.createElement('label');
  prePulseLabel.textContent = 'Pre-Pulse';
  const prePulseSeg = document.createElement('div');
  prePulseSeg.className = 'segmented segmented-4';
  const prePulseBtns: HTMLButtonElement[] = [];
  for (const value of PRE_PULSE_OPTIONS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = `${value}s`;
    btn.dataset.value = String(value);
    if (store.get().prePulseDuration === value) btn.classList.add('active');
    prePulseBtns.push(btn);
    prePulseSeg.appendChild(btn);
  }
  prePulseRow.appendChild(prePulseLabel);
  prePulseRow.appendChild(prePulseSeg);
  section.appendChild(prePulseRow);

  // segmented 全体に 1 個の click listener を張って event delegation。
  // dataset.value から数値復元して store に書き戻す。他ボタンは subscribe で
  // active class の更新を受ける（自発発火でも副作用は class 同期のみ）。
  const onPrePulseClick = (e: MouseEvent): void => {
    const target = e.target;
    if (!(target instanceof HTMLButtonElement)) return;
    const raw = target.dataset.value;
    if (raw === undefined) return;
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    if (store.get().prePulseDuration === value) return;
    store.set({ prePulseDuration: value });
  };
  prePulseSeg.addEventListener('click', onPrePulseClick);

  // ── Detonate Speed dropdown (Phase J) ───────────────────────
  // 5/4/3/2/1/0.5 秒。`.font-picker-row` の grid（label 80px + select 1fr）を
  // そのまま流用し、見た目を Mode dropdown / Font Library と揃える。
  const speedRow = document.createElement('div');
  speedRow.className = 'font-picker-row';
  const speedLabel = document.createElement('label');
  speedLabel.textContent = 'Detonate';
  const speedSelect = document.createElement('select');
  for (const value of DETONATE_DURATION_OPTIONS) {
    const option = document.createElement('option');
    option.value = String(value);
    // 1 桁整数も小数点で統一表記（"2.0s" / "0.5s"）。視認性と秒数比較のため。
    option.textContent = `${value.toFixed(1)}s`;
    if (store.get().detonateDuration === value) option.selected = true;
    speedSelect.appendChild(option);
  }
  speedRow.appendChild(speedLabel);
  speedRow.appendChild(speedSelect);
  section.appendChild(speedRow);

  const onSpeedChange = (): void => {
    const value = Number(speedSelect.value);
    if (!Number.isFinite(value)) return;
    if (store.get().detonateDuration === value) return;
    store.set({ detonateDuration: value });
  };
  speedSelect.addEventListener('change', onSpeedChange);

  // ── Export Mode dropdown (Phase D) ──────────────────────────
  // 書き出しモード: Detonate / Idle Loop / Full Story。Phase J で尺コントロール
  // が前に入ったため、Mode はその下に配置（意味順: 時間 → モード → 実行）。
  const modeRow = document.createElement('div');
  modeRow.className = 'font-picker-row';
  const modeLabel = document.createElement('label');
  modeLabel.textContent = 'Mode';
  const modeSelect = document.createElement('select');
  for (const opt of EXPORT_MODE_OPTIONS) {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    if (opt.value === store.get().exportMode) option.selected = true;
    modeSelect.appendChild(option);
  }
  modeRow.appendChild(modeLabel);
  modeRow.appendChild(modeSelect);
  section.appendChild(modeRow);

  const onModeChange = (): void => {
    // value は union のいずれかであることを EXPORT_MODE_OPTIONS が保証。
    // select.value は string 型のため narrow のために cast する。
    store.set({ exportMode: modeSelect.value as AppState['exportMode'] });
  };
  modeSelect.addEventListener('change', onModeChange);

  // Phase K2 Step 2: GIF 撤去により PNG / WebM の 2 列構成に戻す。既存
  // `.button-row`（2 列 grid）をそのまま使い、`.button-row-3` class は付与
  // しない。CSS 側は `.button-row-3` を Step 2 で削除する。
  const buttonRow = document.createElement('div');
  buttonRow.className = 'button-row';

  const png = document.createElement('button');
  png.type = 'button';
  png.className = 'btn';
  png.textContent = 'PNG';

  const webm = document.createElement('button');
  webm.type = 'button';
  webm.className = 'btn';
  webm.textContent = 'WebM';

  buttonRow.appendChild(png);
  buttonRow.appendChild(webm);
  section.appendChild(buttonRow);

  // Phase I 再設計: 書き出し進捗の表示行。デフォルト hidden で、`setProgress`
  // 呼び出しで初めて表示される。WebM は数秒かかるので「動いているか」の
  // フィードバックとして必要。
  const progress = document.createElement('div');
  progress.className = 'export-progress';
  progress.hidden = true;
  section.appendChild(progress);

  // 外部（例えば shortcut / preset 復元）から値が変わった場合の UI 追従。
  // 自己発火でも副作用は class / value の同期のみで loop しない。
  const unsubscribe = store.subscribe((state, prev) => {
    if (state.exportMode !== prev.exportMode && modeSelect.value !== state.exportMode) {
      modeSelect.value = state.exportMode;
    }
    if (state.prePulseDuration !== prev.prePulseDuration) {
      for (const el of prePulseBtns) {
        const v = Number(el.dataset.value);
        el.classList.toggle('active', v === state.prePulseDuration);
      }
    }
    if (
      state.detonateDuration !== prev.detonateDuration &&
      Number(speedSelect.value) !== state.detonateDuration
    ) {
      speedSelect.value = String(state.detonateDuration);
    }
  });

  let pngCb: (() => void) | null = null;
  let webmCb: (() => void) | null = null;

  const onPngClick = (): void => {
    if (pngCb) {
      pngCb();
    } else {
      // export-agent が wire する前のプレースホルダ。
      // alert は副作用だが、絵文字・装飾を含まないため原則に抵触しない。
      window.alert('PNG export: not implemented yet');
    }
  };
  const onWebMClick = (): void => {
    if (webmCb) {
      webmCb();
    } else {
      window.alert('WebM export: not implemented yet');
    }
  };

  png.addEventListener('click', onPngClick);
  webm.addEventListener('click', onWebMClick);

  root.appendChild(section);

  return {
    destroy: (): void => {
      unsubscribe();
      modeSelect.removeEventListener('change', onModeChange);
      speedSelect.removeEventListener('change', onSpeedChange);
      prePulseSeg.removeEventListener('click', onPrePulseClick);
      png.removeEventListener('click', onPngClick);
      webm.removeEventListener('click', onWebMClick);
      section.remove();
    },
    onExportPng: (cb: () => void): void => {
      pngCb = cb;
    },
    onExportWebM: (cb: () => void): void => {
      webmCb = cb;
    },
    setProgress: (label: string | null): void => {
      if (label === null || label === '') {
        progress.hidden = true;
        progress.textContent = '';
      } else {
        progress.hidden = false;
        progress.textContent = label;
      }
    },
    setBusy: (busy: boolean): void => {
      png.disabled = busy;
      webm.disabled = busy;
      // Mode / 尺コントロールも disable。書き出し途中で切替が走ると plan と
      // 実フレームが食い違って録画結果が壊れる。
      modeSelect.disabled = busy;
      speedSelect.disabled = busy;
      for (const el of prePulseBtns) el.disabled = busy;
    },
  };
}
